import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Dialog from '@components/Dialog';
import useRequest from '@hooks/useRequest';
import useClient from '@hooks/useClient';
import useQueryString from '@hooks/useQueryString';
import useUserActionLog, {
  Enum_Action_Page_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import {
  CapaQuotationServiceType,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import CommonLayout from '@routes/Common/CommonLayout';
import { RequestQueryString } from '@routes/NewRequest/types';
import { requestEnumToString } from '@utils/enumToString';
import RequestProcessMethodPresenter from './RequestProcessMethodPresenter';
import RfqRewriteDialog from './Dialogs/RfqRewriteDialog';
import { UploadedDrawingFileType } from '@modules/request/types';

const RequestProcessMethodContainer = () => {
  const { clientState } = useClient();

  const isAuth = clientState.accessToken ? true : false;

  const [isOpen, setIsOpen] = useState(false);

  const {
    requestState,
    onInitRfqState,
    onUpdateCompleteRfq,
    hasNotEndedRequest,
    lastRequestQuery,
    onUpdateRfqInfo,
    preRfqId
  } = useRequest();

  const {
    query,
    query: { serviceType },
    onAddQueryString,
    onUpdateQueryString
  } = useQueryString<RequestQueryString>();

  const reWrite = query.reWrite === 'true';
  const rfqId = query.rfqId;
  const [rfqRewirteDialogOpen, setRfqRewirteDialogOpen] = useState(reWrite);

  const { data } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId: rfqId ?? ''
    },
    skip: !rfqId,
    fetchPolicy: 'network-only'
  });

  const drawingfiles =
    data?.fetchQuotationUserRfqDetail?.data?.drawingFiles ?? [];
  const uploadedDrawingFiles = drawingfiles.map((file) => {
    const { fileId, fileName } = file;
    return {
      fileId,
      fileName,
      isValid: false
    };
  }) as UploadedDrawingFileType[];

  const { onInsertUserActionLogOne } = useUserActionLog();

  const onRecallLastRequest = useCallback(() => {
    onAddQueryString(lastRequestQuery);
  }, [onAddQueryString, lastRequestQuery]);

  const logType = useMemo(() => {
    if (reWrite) {
      if (serviceType === CapaQuotationServiceType.Cnc) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_CNC_ProcessMethod;
      }

      if (serviceType === CapaQuotationServiceType.Printing) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_PRINTING_ProcessMethod;
      }

      if (serviceType === CapaQuotationServiceType.Inj) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_PRINTING_ProcessMethod;
      }

      if (serviceType === CapaQuotationServiceType.SheetMetal) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_SHEET_METAL_ProcessMethod;
      }

      if (serviceType === CapaQuotationServiceType.Casting) {
        return Enum_Action_Page_Enum.Page_RFQ_Rewrite_CASTING_ProcessMethod;
      }
    }

    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Page_Enum.Page_RFQ_CNC_ProcessMethod;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Page_Enum.Page_RFQ_PRINTING_ProcessMethod;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Page_Enum.Page_RFQ_INJ_ProcessMethod;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Page_Enum.Page_RFQ_SHEET_METAL_ProcessMethod;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Page_Enum.Page_RFQ_CASTING_ProcessMethod;
    }
  }, [serviceType, reWrite]);

  useEffect(() => {
    if (logType) {
      onInsertUserActionLogOne(logType, CapaActivityType.PageVisit);
    }
  }, [onInsertUserActionLogOne, logType]);

  useEffect(() => {
    if (!rfqRewirteDialogOpen && hasNotEndedRequest) {
      setIsOpen(true);
    }
    if (requestState.isComplete) {
      onUpdateCompleteRfq(false);
    }
  }, [
    requestState,
    onUpdateCompleteRfq,
    onInitRfqState,
    hasNotEndedRequest,
    rfqRewirteDialogOpen
  ]);

  const setReWriteRfqInfo = () => {
    setRfqRewirteDialogOpen((prev) => false);

    if (uploadedDrawingFiles.length === 0) {
      onUpdateQueryString({
        reWrite: undefined,
        rfqId: undefined
      });
      return;
    }

    onUpdateRfqInfo({
      uploadedDrawingFiles,
      preRfqId: rfqId
    });
  };

  return (
    <CommonLayout gnbTabs={isAuth ? null : <></>} fullHeight>
      {!isOpen && !preRfqId && (
        <RfqRewriteDialog
          open={rfqRewirteDialogOpen}
          onClose={() => setReWriteRfqInfo()}
        />
      )}

      <Dialog.ConfirmPresenter
        isOpen={!preRfqId && isOpen}
        title={`이전에 진행하던 ${requestEnumToString(
          requestState.serviceType
        )} 요청이 남아있습니다.`}
        description="이전에 진행하던 요청을 불러올까요?"
        onOk={() => {
          setIsOpen(false);
          onRecallLastRequest();
        }}
        okText="불러오기"
        onClose={() => {
          onInitRfqState();
          setIsOpen(false);
        }}
        closeText="이전 요청 삭제"
      />
      <RequestProcessMethodPresenter
        reWrite={reWrite}
        uploadedDrawingFiles={uploadedDrawingFiles}
      />
    </CommonLayout>
  );
};

export default RequestProcessMethodContainer;
